import { useEffect, useState } from "react";

import { useStore } from "../../../Store";
import BackLink from "../../Controls/BackLink";
import { PageLayoutWithGetData } from "../../Controls/PageLayout";
import Section from "../../Controls/Sections";
import { DataDeletionPopUp, DeIdentificationPopUp } from "../PopUps";

import { Box, Button, Heading, HStack, Switch, Text } from "@chakra-ui/react";
import { AxiosRequestConfig } from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useWizard, Wizard } from "react-use-wizard";
import { formatDate } from "../../../utils/formatDate";
import { DetailsAccordion } from "../../Accordion/DetailsAccordion";
import Helpers from "../../Functions/Helpers";
import { WizardFooter } from "../../StepWizard/footer/WizardFooter";
import {
  IndividualConsentData,
  useGetIndividualConsentData,
  useUpdateConsent,
  useWithdrawConsent,
} from "./DashboardLanding/ManagePortalHooks";
import ManagementPortalContainer from "./ManagementPortalContainer";

export default function ReviewWithdraw() {
  const navigate = useNavigate();

  const { consentId } = useParams();

  const { setLoading, setShowPopup, setShowPopupError, setPopupErrorMessage } =
    useStore();

  const [consentData, setConsentData] = useState<IndividualConsentData>();
  const GetManagePortalData = useGetIndividualConsentData(
    setConsentData,
    HandleApiError,
    consentId
  );

  const { deleteMyDataConsentMutation } = useUpdateConsent(
    GetManagePortalData,
    HandleApiError
  );
  const WithdrawConsent = useWithdrawConsent(() => {
    navigate(`/WithdrawalSuccess/${consentId}`);
  }, HandleApiError);

  useEffect(() => {
    setLoading(false);
    if (!consentId) {
      window.location.href = "/DashboardLanding";
    }
    GetManagePortalData();
  }, []);

  const consent = consentData?.consent.consent;
  const softwareProductWording =
    consentData?.softwareProductWording?.softwareProductWording;

  let EndTime = formatDate(consent?.endTime);

  const [PopupDeIdentification, setPopupDeIdentification] = useState(false);
  const [PopupDataDeletion, setPopupDataDeletion] = useState(false);

  function HandleApiError(message: string) {
    setLoading(false);
    setShowPopup(false);
    setShowPopupError(true);
    setPopupErrorMessage(message);
  }

  function firstStep() {
    return (
      <>
        <Box mt={2}>
          <Heading fontSize="md">What happens to your data</Heading>

          {softwareProductWording?.canDeIdentifyData ? (
            <>
              <Text>
                {softwareProductWording?.withdrawalImpactWhatHappensToYourData}
              </Text>
              <DeIdentificationPopUp
                showPopup={PopupDeIdentification}
                handleClose={() => setPopupDeIdentification(false)}
              />

              <Button
                mt={2}
                variant="link"
                onClick={() => {
                  setPopupDeIdentification(true);
                }}
              >
                How and why we de-identify data
              </Button>

              <HStack my={5} justifyContent="space-between">
                <Heading fontSize="sm">Delete my data instead</Heading>
                <Switch
                  size="md"
                  aria-labelledby="Delete My Data"
                  isChecked={Boolean(consent?.deleteMyData)}
                  onChange={() => {
                    deleteMyDataConsentMutation(consentId, consent);
                  }}
                />
              </HStack>

              <Text>
                If you don't do this before {EndTime}, your data will be
                de-identified.
              </Text>
            </>
          ) : (
            <>
              <Text>We will delete all your data.</Text>
            </>
          )}

          <DataDeletionPopUp
            showPopup={PopupDataDeletion}
            handleClose={() => setPopupDataDeletion(false)}
          />

          <Button
            mt={2}
            variant="link"
            onClick={() => {
              setPopupDataDeletion(true);
            }}
          >
            See how we delete your data
          </Button>
        </Box>
      </>
    );
  }

  function secondStep() {
    return (
      <>
        <Box mt={2}>
          <Heading fontSize="md">Impacts to you service</Heading>
          <Text>{softwareProductWording?.withdrawalImpactToService}</Text>

          <Box id="accordionAdditionalUses">
            <DetailsAccordion title="Show detailed impacts to my service">
              {/* <hr /> */}

              {/* Individual */}
              {consent?.customerType === "Individual" ? (
                <>
                  {/* Name and occupation */}
                  {consent?.nameAndOccupationConsent ===
                    "RequestedAndConsented" &&
                  consent?.nameOccupationContactDetailsConsent !==
                    "RequestedAndConsented" ? (
                    <>
                      <Heading fontSize="sm">Name and occupation</Heading>
                      <Text>
                        {
                          softwareProductWording?.withdrawalImpactNameAndOccupation
                        }
                      </Text>
                    </>
                  ) : null}

                  {/* Contact details  */}
                  {consent?.contactDetailsConsent === "RequestedAndConsented" &&
                  consent?.nameOccupationContactDetailsConsent !==
                    "RequestedAndConsented" ? (
                    <>
                      <Heading fontSize="sm">Contact details</Heading>
                      <Text>
                        {softwareProductWording?.withdrawalImpactContactDetails}
                      </Text>
                    </>
                  ) : null}

                  {/* Name, occupation and contact details  */}
                  {consent?.nameOccupationContactDetailsConsent ===
                  "RequestedAndConsented" ? (
                    <>
                      <Heading fontSize="sm">
                        Name, occupation and contact details
                      </Heading>
                      <Text>
                        {
                          softwareProductWording?.withdrawalImpactNameOccupationContactDetails
                        }
                      </Text>
                    </>
                  ) : null}
                </>
              ) : null}

              {/* Business */}
              {consent?.customerType === "Business" ? (
                <>
                  {/* Organisation profile   */}
                  {consent?.organisationProfileConsent ===
                    "RequestedAndConsented" &&
                  consent?.organisationProfileAndContactDetailsConsent !==
                    "RequestedAndConsented" ? (
                    <>
                      <Heading fontSize="sm">Organisation profile</Heading>
                      <Text>
                        {
                          softwareProductWording?.withdrawalImpactOrganisationProfile
                        }
                      </Text>
                    </>
                  ) : null}

                  {/* Organisation contact details  */}
                  {consent?.organisationContactDetailsConsent ===
                    "RequestedAndConsented" &&
                  consent?.organisationProfileAndContactDetailsConsent !==
                    "RequestedAndConsented" ? (
                    <>
                      <Heading fontSize="sm">
                        Organisation contact details
                      </Heading>
                      <Text>
                        {
                          softwareProductWording?.withdrawalImpactOrganisationContactDetails
                        }
                      </Text>
                    </>
                  ) : null}

                  {/* Organisation profile and contact details  */}
                  {consent?.organisationProfileAndContactDetailsConsent ===
                  "RequestedAndConsented" ? (
                    <>
                      <Heading fontSize="sm">
                        Organisation profile and contact details
                      </Heading>
                      <Text>
                        {
                          softwareProductWording?.withdrawalImpactOrganisationProfileAndContactDetails
                        }
                      </Text>
                    </>
                  ) : null}
                </>
              ) : null}

              {/* Account name, type and balance  */}
              {consent?.accountNameTypeAndBalanceConsent ===
                "RequestedAndConsented" &&
              consent?.accountBalanceAndDetailsConsent !==
                "RequestedAndConsented" ? (
                <>
                  <Heading fontSize="sm">
                    Account name, type and balance
                  </Heading>
                  <Text>
                    {
                      softwareProductWording?.withdrawalImpactAccountNameTypeAndBalance
                    }
                  </Text>
                </>
              ) : null}

              {/* Account numbers and features  */}
              {consent?.accountNumbersAndFeaturesConsent ===
                "RequestedAndConsented" &&
              consent?.accountBalanceAndDetailsConsent !==
                "RequestedAndConsented" ? (
                <>
                  <Heading fontSize="sm">Account numbers and features</Heading>
                  <Text>
                    {
                      softwareProductWording?.withdrawalImpactAccountNumbersAndFeatures
                    }
                  </Text>
                </>
              ) : null}

              {/* Account balance and details */}
              {consent?.accountBalanceAndDetailsConsent ===
              "RequestedAndConsented" ? (
                <>
                  <Heading fontSize="sm">Account balance and details</Heading>
                  <Text>
                    {
                      softwareProductWording?.withdrawalImpactAccountBalanceAndDetails
                    }
                  </Text>
                </>
              ) : null}

              {/* Transaction details */}
              {consent?.transactionDetailsConsent ===
              "RequestedAndConsented" ? (
                <>
                  <Heading fontSize="sm">Transaction details</Heading>
                  <Text>
                    {softwareProductWording?.withdrawalImpactTransactionDetails}
                  </Text>
                </>
              ) : null}

              {/* Direct debits and scheduled payments */}
              {consent?.directDebitsAndScheduledPaymentsConsent ===
              "RequestedAndConsented" ? (
                <>
                  <Heading fontSize="sm">
                    Direct debits and scheduled payments
                  </Heading>
                  <Text>
                    {
                      softwareProductWording?.withdrawalImpactDirectDebitsAndScheduledPayments
                    }
                  </Text>
                </>
              ) : null}

              {/* Saved payees */}
              {consent?.savedPayeesConsent === "RequestedAndConsented" ? (
                <>
                  <Heading fontSize="sm">Saved payees</Heading>
                  <Text>
                    {softwareProductWording?.withdrawalImpactSavedPayees}
                  </Text>
                </>
              ) : null}
            </DetailsAccordion>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <ManagementPortalContainer>
      <Wizard
        header={<ReviewWizardHeader consentId={consentId} />}
        footer={
          <ReviewWithdrawFooter onWithdraw={() => WithdrawConsent(consent)} />
        }
      >
        <StepWrapper>{firstStep()}</StepWrapper>
        <StepWrapper>{secondStep()}</StepWrapper>
      </Wizard>
    </ManagementPortalContainer>
  );
}

const StepWrapper = ({ children }: { children: JSX.Element }) => (
  <Box py={20} px={10} minHeight={"400px"}>
    {children}
  </Box>
);

const ReviewWizardHeader = (props: { consentId?: string }) => {
  const navigate = useNavigate();
  const { previousStep, activeStep } = useWizard();

  return (
    <Box px={10} pt={6}>
      <BackLink
        handleOnClick={() => {
          if (activeStep === 0) {
            navigate(`/DataSharingArrangement/${props.consentId}`);
          } else {
            previousStep();
          }
        }}
        page="/DataSharingArrangement"
      />

      <Heading variant="pageHeading">When you stop sharing</Heading>
    </Box>
  );
};

const ReviewWithdrawFooter = ({ onWithdraw }: { onWithdraw: () => void }) => {
  const { stepCount, activeStep, goToStep, nextStep, previousStep } =
    useWizard();
  const navigate = useNavigate();

  return (
    <Section isGrey={true}>
      <WizardFooter
        onCancel={() => {
          if (activeStep === 0) {
            navigate("/DataSharingArrangement");
          } else {
            previousStep();
          }
        }}
        onNext={() => {
          if (activeStep === 1) {
            onWithdraw();
          } else {
            nextStep();
          }
        }}
        canGoNext={true}
        goToStep={goToStep}
        activeStep={activeStep}
        stepCount={stepCount}
        cancelButtonText={activeStep === 0 ? "Cancel" : "No, go back"}
        nextButtonText={activeStep === 0 ? "Continue" : "Yes, stop sharing"}
      />
    </Section>
  );
};
